import { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import {
    PackageIcon,
    PlusIcon,
    CreditCardIcon,
    PencilIcon,
    ArchiveIcon,
} from "lucide-react";
import AuthLayout from "@container/layout/auth";
import AdminSubscriptionPackageForm from "@container/admin-subscription-package-form";
import ArchivePackageConfirmationDialog from "@container/dialog/dialog-archive-package-confirm";
import { Card } from "@component/card";
import { Button } from "@component/button";
import { Pagination } from "@component/pagination";
import { EmptyState } from "@component/empty-state";
import { Tabs, TabsList, TabsTrigger } from "@component/tabs";
import {
    listAdminPackages,
    archiveAdminPackage,
    ADMIN_PACKAGE_PLATFORMS,
    AdminPackageError,
} from "@service/admin-subscription-packages";
import type {
    AdminSubscriptionPackage,
    AdminPackagePlatform,
} from "@service/admin-subscription-packages";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";

// Large full-page table → 20 per page (house style §7, P1).
const PER_PAGE = 20;

const PLATFORM_LABEL: Record<AdminPackagePlatform, string> = {
    recruiter: "Recruiter",
    training: "Training",
    agent: "Operator",
};

// Preferred currency order for the single representative price shown in the list.
const PRICE_CURRENCY_ORDER = ["SGD", "MYR", "IDR", "USD"];

// Reads a representative monthly price out of the `details` map. The BE shape is
// PER-CURRENCY: `details = { SGD: { price_month, price, … }, MYR: {…}, IDR: {…} }`.
// We pick the first currency present (preferred order, then any) and read its
// `price_month` (falling back to `price`). A legacy flat `{ price_month, price,
// currency }` shape is still tolerated.
function readPrice(details: Record<string, unknown>): {
    amount: number | null;
    currency: string | null;
} {
    const pickAmount = (entry: Record<string, unknown>): number | null => {
        for (const key of ["price_month", "price"]) {
            const v = entry[key];
            if (typeof v === "number" && Number.isFinite(v)) return v;
        }
        return null;
    };

    // Per-currency shape: details[CCY] is an object with the price fields.
    const currencyKeys = Object.keys(details).filter(
        (k) => details[k] != null && typeof details[k] === "object"
    );
    if (currencyKeys.length > 0) {
        const ordered = [
            ...PRICE_CURRENCY_ORDER.filter((c) => currencyKeys.includes(c)),
            ...currencyKeys.filter((c) => !PRICE_CURRENCY_ORDER.includes(c)),
        ];
        for (const ccy of ordered) {
            const amount = pickAmount(details[ccy] as Record<string, unknown>);
            if (amount !== null) return { amount, currency: ccy };
        }
    }

    // Legacy flat shape: { price_month, price, currency }.
    const currency =
        typeof details.currency === "string" ? details.currency : null;
    return { amount: pickAmount(details), currency };
}

function formatAmount(amount: number): string {
    return amount.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
}

function formatPrice(details: Record<string, unknown>): string | null {
    const { amount, currency } = readPrice(details);
    if (amount === null) return null;
    if (amount === 0) return "Free";
    return `${currency ? `${currency} ` : ""}${formatAmount(amount)}/mo`;
}

// Every currency the package is priced in (monthly price > 0), in preferred
// order — used to list ALL currencies in the Price column. Currencies with no
// usable monthly price (0 / absent) are skipped.
function readAllMonthlyPrices(
    details: Record<string, unknown>
): { currency: string; amount: number }[] {
    const pickAmount = (entry: Record<string, unknown>): number | null => {
        for (const key of ["price_month", "price"]) {
            const v = entry[key];
            if (typeof v === "number" && Number.isFinite(v)) return v;
        }
        return null;
    };
    const currencyKeys = Object.keys(details).filter(
        (k) => details[k] != null && typeof details[k] === "object"
    );
    const ordered = [
        ...PRICE_CURRENCY_ORDER.filter((c) => currencyKeys.includes(c)),
        ...currencyKeys.filter((c) => !PRICE_CURRENCY_ORDER.includes(c)),
    ];
    const out: { currency: string; amount: number }[] = [];
    for (const ccy of ordered) {
        const amount = pickAmount(details[ccy] as Record<string, unknown>);
        if (amount !== null && amount > 0) out.push({ currency: ccy, amount });
    }
    return out;
}

// Human label for the package billing duration (BE `duration`, in months).
// Returns null when unset so the cell renders the standard "—" placeholder.
function formatDuration(duration: number | null): string | null {
    if (duration == null || duration <= 0) return null;
    return `${duration} ${duration === 1 ? "month" : "months"}`;
}

// Short human summary of the free-form `features` map for the table cell, e.g.
// "Cv Screening 500 · Ai Assessment 100 · +2 more".
function summarizeFeatures(features: Record<string, unknown>): string | null {
    const entries = Object.entries(features ?? {});
    if (entries.length === 0) return null;
    const humanize = (key: string) =>
        key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const parts = entries.slice(0, 2).map(([key, value]) => {
        const label = humanize(key);
        if (typeof value === "boolean") return value ? label : `No ${label}`;
        if (typeof value === "number") return `${label} ${value}`;
        return label;
    });
    const extra = entries.length - parts.length;
    if (extra > 0) parts.push(`+${extra} more`);
    return parts.join(" · ");
}

// Thin shell — keeps AuthLayout at the top, all logic in the content component.
export default function AdminSubscriptionPackagesPage() {
    return (
        <AuthLayout>
            <AdminSubscriptionPackagesContent />
        </AuthLayout>
    );
}

function AdminSubscriptionPackagesContent() {
    const [packages, setPackages] = useState<AdminSubscriptionPackage[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const [page, setPage] = useState(1);
    // Platform is selected via tabs (Recruiter / Training / Operator) — always a
    // real platform, so the list is always scoped to one platform.
    const [platformFilter, setPlatformFilter] =
        useState<AdminPackagePlatform>("recruiter");

    // Create/edit form drawer state. `formPkg` null = create, a package = edit.
    const [formOpen, setFormOpen] = useState(false);
    const [formPkg, setFormPkg] = useState<AdminSubscriptionPackage | null>(
        null
    );
    // Archive confirmation state. `archivePkg` = the package pending archive
    // (null = dialog closed); `archiving` = the DELETE request is in flight.
    const [archivePkg, setArchivePkg] =
        useState<AdminSubscriptionPackage | null>(null);
    const [archiving, setArchiving] = useState(false);
    // Bumped to trigger a refetch after a create/edit/stripe-sync/archive.
    const [reloadKey, setReloadKey] = useState(0);

    // Fetch the current page. Platform + pagination are server-side. There is no
    // text-search box: the list endpoint (BE #3197) has no query param, and a
    // client-side filter over only the current page would silently hide matches
    // on other pages — so search is omitted until the BE supports it.
    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);
        listAdminPackages({
            platform: platformFilter,
            includeArchived: true,
            page,
            pageSize: PER_PAGE,
        })
            .then((res) => {
                if (!active) return;
                setPackages(res.items);
                setTotal(res.total);
                setLoading(false);
            })
            .catch((err: unknown) => {
                if (!active) return;
                const e = err instanceof Error ? err : new Error(String(err));
                catchErrorLog(`[admin-packages] list load failed: ${e.message}`);
                setError(e);
                setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [page, platformFilter, reloadKey]);

    // Reset to page 1 whenever the platform tab changes.
    function changePlatform(value: string) {
        setPlatformFilter(value as AdminPackagePlatform);
        setPage(1);
    }

    const totalPages = Math.ceil(total / PER_PAGE);

    // Open the create form (no package = create mode).
    function handleNewPackage() {
        setFormPkg(null);
        setFormOpen(true);
    }

    // Open the edit form for a row.
    const handleEditPackage = useCallback((pkg: AdminSubscriptionPackage) => {
        setFormPkg(pkg);
        setFormOpen(true);
    }, []);

    // Open the archive-confirmation dialog for a row.
    const handleArchivePackage = useCallback(
        (pkg: AdminSubscriptionPackage) => {
            setArchivePkg(pkg);
        },
        []
    );

    // Run the soft-archive (BE #3197 DELETE). On success: close the dialog,
    // bump reloadKey so the row re-fetches and flips to the "Archived" chip.
    const handleConfirmArchive = useCallback(
        async (pkg: AdminSubscriptionPackage) => {
            setArchiving(true);
            try {
                await archiveAdminPackage(pkg.id);
                toast.success(`${pkg.packageName} archived.`);
                setArchivePkg(null);
                setReloadKey((k) => k + 1);
            } catch (err) {
                const message =
                    err instanceof AdminPackageError
                        ? err.message
                        : "Could not archive the package.";
                toast.error(message);
            } finally {
                setArchiving(false);
            }
        },
        []
    );

    // Refresh the list after a successful create / edit / stripe-sync.
    const handleSaved = useCallback(() => {
        setReloadKey((k) => k + 1);
    }, []);

    return (
        <div>
            {/* Page header */}
            <div className="flex items-start justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                        <PackageIcon className="w-5 h-5" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">
                            Subscription Packages
                        </h1>
                        <p className="text-sm text-gray-600 mt-1">
                            Manage the subscription packages offered per platform.
                        </p>
                    </div>
                </div>
                <Button onClick={handleNewPackage} className="flex-shrink-0 mt-1">
                    <PlusIcon className="w-4 h-4 mr-1.5" />
                    New package
                </Button>
            </div>

            {/* Platform tabs — one tab per platform (Recruiter / Training / Operator) */}
            <Tabs value={platformFilter} onValueChange={changePlatform} className="mb-4">
                <TabsList className="flex w-full bg-transparent h-auto p-0 border-b border-gray-200 justify-start rounded-none">
                    {ADMIN_PACKAGE_PLATFORMS.map((platform) => (
                        <TabsTrigger
                            key={platform}
                            value={platform}
                            className="text-sm bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
                        >
                            {PLATFORM_LABEL[platform]}
                        </TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>

            {/* Table / states */}
            {error ? (
                <Card>
                    <div className="px-6 py-10 text-center">
                        <p className="text-sm text-gray-900 font-medium mb-1">
                            Could not load packages.
                        </p>
                        <p className="text-xs text-gray-500">
                            The error has been reported. Try refreshing the page.
                        </p>
                    </div>
                </Card>
            ) : loading ? (
                <Card>
                    <div className="p-5">
                        <SkeletonTable />
                    </div>
                </Card>
            ) : packages.length === 0 ? (
                <Card>
                    <EmptyState
                        icon={<PackageIcon className="h-12 w-12 text-gray-300" />}
                        title={`No ${PLATFORM_LABEL[platformFilter]} packages yet.`}
                        description="Create a package to start offering subscriptions for this platform, or switch to another platform tab."
                        action={
                            <Button onClick={handleNewPackage}>
                                <PlusIcon className="w-4 h-4 mr-1.5" />
                                New package
                            </Button>
                        }
                    />
                </Card>
            ) : (
                <>
                    <Card className="overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-max">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <th className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                            Package
                                        </th>
                                        <th className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                            Price
                                        </th>
                                        <th className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                            Duration
                                        </th>
                                        <th className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                            Features
                                        </th>
                                        <th className="h-[45px] px-4 py-3 w-px text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                            Stripe
                                        </th>
                                        <th className="h-[45px] px-4 py-3 w-px text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                            Status
                                        </th>
                                        <th className="h-[45px] px-4 py-3 w-px text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {packages.map((pkg) => (
                                        <PackageRow
                                            key={pkg.id}
                                            pkg={pkg}
                                            onEdit={handleEditPackage}
                                            onArchive={handleArchivePackage}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>

                    <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        total={total}
                        pageSize={PER_PAGE}
                        itemLabel="package"
                        onPageChange={setPage}
                        className="mt-4"
                    />
                </>
            )}

            {/* Create / edit drawer (#2654) */}
            <AdminSubscriptionPackageForm
                open={formOpen}
                onClose={() => setFormOpen(false)}
                pkg={formPkg}
                onSaved={handleSaved}
            />

            {/* Archive confirmation (#2655) */}
            <ArchivePackageConfirmationDialog
                isOpen={archivePkg !== null}
                pkg={archivePkg}
                archiving={archiving}
                onClose={() => setArchivePkg(null)}
                onConfirm={handleConfirmArchive}
            />
        </div>
    );
}

function PackageRow({
    pkg,
    onEdit,
    onArchive,
}: {
    pkg: AdminSubscriptionPackage;
    onEdit: (pkg: AdminSubscriptionPackage) => void;
    onArchive: (pkg: AdminSubscriptionPackage) => void;
}) {
    // All priced currencies (monthly > 0); `fallbackPrice` covers Free / single.
    const prices = readAllMonthlyPrices(pkg.details);
    const fallbackPrice = formatPrice(pkg.details);
    const duration = formatDuration(pkg.duration);
    const features = summarizeFeatures(pkg.features);
    // Synced if EITHER the legacy SG mirror (stripeProductId) OR the per-region
    // maps (#2654: stripeProductIds/stripePriceIds) are populated — a package
    // synced only to the MY account has null stripeProductId but a valid
    // stripeProductIds.MY, so the SG mirror alone under-reports sync state.
    // Mirrors the same predicate used in admin-subscription-package-form.tsx.
    const stripeSynced = Boolean(
        pkg.stripeProductId ||
            (pkg.stripeProductIds &&
                Object.keys(pkg.stripeProductIds).length > 0) ||
            (pkg.stripePriceIds && Object.keys(pkg.stripePriceIds).length > 0)
    );

    return (
        <tr
            className="hover:bg-gray-50 transition-colors cursor-pointer"
            onClick={() => onEdit(pkg)}
        >
            {/* Package — identity icon + name + platform subtext */}
            <td className="px-4 py-4 whitespace-nowrap">
                <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50">
                        <PackageIcon className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                            {pkg.packageName}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 truncate">
                            {PLATFORM_LABEL[pkg.platform] ?? pkg.platform}
                        </div>
                    </div>
                </div>
            </td>

            {/* Price — every currency the package is priced in, stacked */}
            <td className="px-4 py-4 whitespace-nowrap">
                {prices.length > 0 ? (
                    <div className="space-y-0.5">
                        {prices.map((p) => (
                            <div
                                key={p.currency}
                                className="text-sm text-gray-900 tabular-nums"
                            >
                                {p.currency} {formatAmount(p.amount)}
                                <span className="text-gray-400">/mo</span>
                            </div>
                        ))}
                    </div>
                ) : fallbackPrice ? (
                    <span className="text-sm text-gray-900 tabular-nums">
                        {fallbackPrice}
                    </span>
                ) : (
                    <span className="text-gray-400">—</span>
                )}
            </td>

            {/* Duration (months) */}
            <td className="px-4 py-4 whitespace-nowrap">
                {duration ? (
                    <span className="text-sm text-gray-700 tabular-nums">
                        {duration}
                    </span>
                ) : (
                    <span className="text-gray-400">—</span>
                )}
            </td>

            {/* Features summary */}
            <td className="px-4 py-4 max-w-md">
                {features ? (
                    <p className="text-xs text-gray-500 leading-relaxed truncate">
                        {features}
                    </p>
                ) : (
                    <span className="text-gray-400">—</span>
                )}
            </td>

            {/* Stripe sync chip */}
            <td className="px-4 py-4 w-px whitespace-nowrap text-center">
                <span
                    className={cn(
                        "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium border",
                        stripeSynced
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-gray-50 text-gray-500 border-gray-200"
                    )}
                >
                    <CreditCardIcon className="w-3 h-3" />
                    {stripeSynced ? "Synced" : "Not synced"}
                </span>
            </td>

            {/* Status chip (+ "Hidden" when the package is a shadow package —
                excluded from the public catalog, #3004) */}
            <td className="px-4 py-4 w-px whitespace-nowrap text-center">
                <span className="inline-flex items-center gap-1.5">
                    <span
                        className={cn(
                            "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border",
                            pkg.isActive
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : "bg-gray-50 text-gray-500 border-gray-200"
                        )}
                    >
                        {pkg.isActive ? "Active" : "Archived"}
                    </span>
                    {pkg.isShadowPackage && (
                        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border bg-amber-50 text-amber-700 border-amber-200">
                            Hidden
                        </span>
                    )}
                </span>
            </td>

            {/* Actions — Edit + Archive (#2655). Archive only for active
                packages; an already-archived package re-activates via Edit. */}
            <td className="px-4 py-4 w-px whitespace-nowrap text-center">
                <div className="flex items-center justify-center gap-1">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit(pkg);
                        }}
                        aria-label={`Edit ${pkg.packageName}`}
                    >
                        <PencilIcon className="w-3.5 h-3.5 mr-1" />
                        Edit
                    </Button>
                    {pkg.isActive && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                onArchive(pkg);
                            }}
                            aria-label={`Archive ${pkg.packageName}`}
                            className="text-gray-400 hover:text-rose-600 hover:bg-rose-50"
                        >
                            <ArchiveIcon className="w-3.5 h-3.5 mr-1" />
                            Archive
                        </Button>
                    )}
                </div>
            </td>
        </tr>
    );
}

function SkeletonTable() {
    return (
        <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-6">
                    <div className="h-9 w-9 bg-gray-100 rounded-lg animate-pulse flex-shrink-0" />
                    <div className="space-y-1.5 w-[180px]">
                        <div className="h-3.5 w-36 bg-gray-100 rounded animate-pulse" />
                        <div className="h-3 w-20 bg-gray-100 rounded animate-pulse" />
                    </div>
                    <div className="h-3.5 w-16 bg-gray-100 rounded animate-pulse" />
                    <div className="flex-1 space-y-1">
                        <div className="h-3 w-3/4 bg-gray-100 rounded animate-pulse" />
                    </div>
                    <div className="h-5 w-20 bg-gray-100 rounded-full animate-pulse" />
                    <div className="h-5 w-16 bg-gray-100 rounded-full animate-pulse" />
                </div>
            ))}
        </div>
    );
}
